'use client';

import { useEffect } from 'react';
import { SHORTCUTS, detectPlatform, formatShortcut, type Shortcut, type ShortcutGroup } from './shortcut-definitions';

export interface ShortcutsCheatsheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ShortcutsCheatsheet({ isOpen, onClose }: ShortcutsCheatsheetProps) {
  const platform = detectPlatform();

  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const grouped = SHORTCUTS.reduce<Map<ShortcutGroup, Shortcut[]>>((acc, s) => {
    if (!acc.has(s.group)) acc.set(s.group, []);
    acc.get(s.group)!.push(s);
    return acc;
  }, new Map());

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
      data-tour="shortcuts-cheatsheet"
      className="fixed inset-0 z-[78] flex items-center justify-center bg-slate-900/55 px-4 py-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]">
        <header className="flex items-center justify-between border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-6 py-3">
          <h2 id="shortcuts-title" className="text-lg font-semibold text-[var(--k-color-text-primary)]">
            Tastatur-Übersicht
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-md p-1 text-[var(--k-color-text-muted)] transition hover:bg-[var(--k-color-surface)]"
          >
            ×
          </button>
        </header>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {Array.from(grouped.entries()).map(([group, items]) => (
            <section key={group} className="mb-6 last:mb-0">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--k-color-text-muted)]">
                {group}
              </h3>
              <ul className="space-y-2">
                {items.map((s) => (
                  <li key={s.id} className="flex items-start justify-between gap-3 rounded-[var(--k-radius-md)] px-2 py-1.5 hover:bg-[var(--k-color-surface-muted)]">
                    <div>
                      <p className="text-sm font-medium text-[var(--k-color-text-primary)]">{s.action}</p>
                      {s.description ? (
                        <p className="text-xs text-[var(--k-color-text-secondary)]">{s.description}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {s.keys.map((key, idx) => (
                        <kbd
                          key={idx}
                          className="rounded border border-[var(--k-color-border-default)] bg-[var(--k-color-surface-muted)] px-2 py-0.5 font-mono text-xs text-[var(--k-color-text-primary)]"
                        >
                          {formatShortcut([key], platform)}
                        </kbd>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <footer className="border-t border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] px-6 py-3 text-center text-xs text-[var(--k-color-text-muted)]">
          Drücke <kbd className="rounded border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-1 font-mono">?</kbd> jederzeit, um diese Übersicht zu öffnen.
        </footer>
      </div>
    </div>
  );
}
